import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { authStore } from '../../shared/store/auth.store.js'
import { ROUTES } from '../../app/routes.js'
import { ApiError } from '../../shared/types/api.js'

interface FormErrors {
  email?: string
  password?: string
  general?: string
}

export default function LoginPage() {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)

  function validate(): boolean {
    const e: FormErrors = {}
    if (!email.trim()) e.email = t('error.emailRequired')
    else if (!/^[^\s@]+@[^\s@]+$/.test(email.trim())) e.email = t('error.emailInvalid')
    if (!password) e.password = t('error.passwordRequired')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      await authStore.login(email.trim(), password)
      // GuestGuard opens the orders workspace once auth status becomes 'authenticated'.
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 401) {
        setErrors({ general: t('error.invalidCredentials') })
      } else if (err instanceof ApiError && err.status === 429) {
        setErrors({ general: t('error.generic') })
      } else if (err instanceof ApiError) {
        setErrors({ general: err.message || t('error.serverError') })
      } else {
        setErrors({ general: t('error.network') })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-logo">Magic Food</h1>
          <p className="auth-subtitle">{t('auth.login')}</p>
        </div>

        {errors.general && (
          <div className="form-error-banner">{errors.general}</div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setErrors({}) }}
              placeholder="user@example.com"
              autoComplete="email"
              required
              disabled={loading}
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-field">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setErrors({}) }}
              autoComplete="current-password"
              required
              disabled={loading}
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>

        <p className="auth-switch">
          {t('auth.noAccount')}
          {' '}<a href={ROUTES.REGISTER}>{t('auth.registerLink')}</a>
        </p>
      </div>
    </div>
  )
}